import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/practice", label: "Practice" },
  { to: "/sectors", label: "Sectors" },
  { to: "/approach", label: "Approach" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" }
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-50 bg-bone/95 backdrop-blur-sm transition-all duration-300 ${scrolled ? "border-b border-rule" : "border-b border-transparent"}`}
      data-testid="site-header"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-center justify-between h-28 md:h-36">
          <Link to="/" className="flex items-center group" data-testid="logo-link" onClick={() => setOpen(false)}>
            <img
              src="https://customer-assets.emergentagent.com/job_dugavu-staging/artifacts/t29z1os5_Dugavu%20logo%203.png"
              alt="Dugavu Ventures"
              className="h-24 md:h-32 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-[13px] tracking-wide transition-colors duration-200 py-2 ${
                    isActive
                      ? "text-ink font-medium after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:bg-oxblood"
                      : "text-slate hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <a
              href="mailto:mandates@dugavuventures.com"
              data-testid="header-mandate-cta"
              className="inline-flex items-center bg-oxblood text-white px-5 py-3 text-[12px] tracking-widest uppercase font-medium hover:bg-oxblood-hover transition-colors duration-300"
            >
              Discuss a mandate
            </a>
          </div>

          <button
            className="lg:hidden p-2 -mr-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} weight="thin" /> : <List size={24} weight="thin" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-rule bg-bone" data-testid="mobile-nav">
          <nav className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-serif text-2xl py-3 border-b border-rule flex items-center justify-between ${isActive ? "text-ink" : "text-slate"}`
                }
              >
                <span>{item.label}</span>
                {/* Active marker for mobile */}
                <span aria-hidden="true" className="ml-3 h-px w-6 transition-all">
                  <span className="block h-px w-full bg-current" />
                </span>
              </NavLink>
            ))}
            <a
              href="mailto:mandates@dugavuventures.com"
              data-testid="mobile-mandate-cta"
              className="mt-6 inline-flex items-center justify-center bg-oxblood text-white px-5 py-4 text-[12px] tracking-widest uppercase font-medium"
            >
              Discuss a mandate
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
